import { useMemo, useState } from 'react';
import { useAction, useResource } from '@limuzin/kit';
import type { SavedPlaceView } from '@limuzin/passenger';
import { addSavedPlace, getSavedPlaces, removeSavedPlace, updateSavedPlace } from '../api';
import { useHistory, useSession } from '../session';
import { Icon } from '../material';
import { Button, ErrorNote, IconButton, Panel, Soft, Spinner, TitledScreen } from '../widgets';
import { AddressField } from './address-field';
import { isNamed } from './draft';

/**
 * «Мои адреса» — shortcuts the passenger names themselves. A port of
 * `addresses_screen.dart`.
 *
 * These are not the town catalogue: «Дом» is not a place the operator knows about,
 * and it does not have to be. The picker offers both, saved ones first.
 */
export function AddressesScreen() {
  const { api } = useSession();
  const saved = useResource<SavedPlaceView[]>(() => getSavedPlaces(api), [api]);
  const history = useHistory();

  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState('');

  const create = useAction(async () => {
    if (!label.trim() || !address.trim()) return;
    await addSavedPlace(api, label.trim(), address.trim());
    setAdding(false);
    setLabel('');
    setAddress('');
    saved.reload();
  });

  const openForm = (value = '') => {
    setAdding(true);
    setOpenId(null);
    setLabel('');
    setAddress(value);
    create.clearError();
  };

  /**
   * «Другие адреса» — places this passenger keeps going to that are not saved yet,
   * counted from their own history. The point is that saving one takes a tap rather
   * than typing.
   */
  const frequent = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ride of history) {
      for (const stop of ride.stops) {
        const value = stop.address.trim();
        if (value && isNamed(stop)) counts.set(value, (counts.get(value) ?? 0) + 1);
      }
    }
    const known = new Set(
      (saved.data ?? []).flatMap((p) => [p.label.toLowerCase(), p.address.toLowerCase()]),
    );
    return [...counts.entries()]
      .filter(([value]) => !known.has(value.toLowerCase()))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([value]) => value);
  }, [history, saved.data]);

  return (
    <TitledScreen title="Мои адреса" subtitle="Сохранённые места для быстрого заказа">
      {saved.loading ? (
        <Spinner />
      ) : saved.error && !saved.data ? (
        <p className="body" style={{ textAlign: 'center', padding: 24, margin: 0 }}>
          {saved.error.message}
        </p>
      ) : (
        <div className="column screen-list">
          <div className="stack" style={{ gap: 10 }}>
            {(saved.data ?? []).map((place) => (
              <SavedCard
                key={place.id}
                place={place}
                open={openId === place.id}
                onToggle={() => {
                  setOpenId(openId === place.id ? null : place.id);
                  setAdding(false);
                }}
                onChanged={() => {
                  setOpenId(null);
                  saved.reload();
                }}
              />
            ))}

            {adding ? (
              <Panel radius={18} style={{ padding: 16 }}>
                <div className="strong" style={{ fontWeight: 700, fontSize: 14.5 }}>
                  Новый адрес
                </div>
                <input
                  className="sfield"
                  style={{ marginTop: 10 }}
                  placeholder="Название — «Дом»"
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                />
                <div style={{ marginTop: 8 }}>
                  <AddressField placeholder="Адрес или место" value={address} onChange={setAddress} />
                </div>
                <ErrorNote error={create.error} />
                <div className="row" style={{ marginTop: 10, gap: 8 }}>
                  <Soft tone="quiet" style={{ padding: '13px 16px' }} onClick={() => setAdding(false)}>
                    Отмена
                  </Soft>
                  <Button
                    size="field"
                    className="grow"
                    busy={create.pending}
                    disabled={!label.trim() || !address.trim()}
                    onClick={() => void create.run()}
                  >
                    Сохранить
                  </Button>
                </div>
              </Panel>
            ) : (
              <button type="button" className="dashed-add" onClick={() => openForm()}>
                + Добавить адрес
              </button>
            )}
          </div>

          {frequent.length > 0 && (
            <>
              <div className="label" style={{ padding: '26px 2px 12px' }}>
                Другие адреса
              </div>
              <div className="stack" style={{ gap: 8 }}>
                {frequent.map((value) => (
                  <Panel key={value} radius={16} style={{ padding: '12px 14px' }}>
                    <div className="row" style={{ gap: 12 }}>
                      <span className="grow ellipsis strong" style={{ fontSize: 14 }}>
                        {value}
                      </span>
                      <Soft style={{ padding: '9px 14px' }} onClick={() => openForm(value)}>
                        Добавить
                      </Soft>
                    </div>
                  </Panel>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </TitledScreen>
  );
}

function SavedCard({
  place,
  open,
  onToggle,
  onChanged,
}: {
  place: SavedPlaceView;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const { api } = useSession();
  const [label, setLabel] = useState(place.label);
  const [address, setAddress] = useState(place.address);

  const save = useAction(async () => {
    await updateSavedPlace(api, place.id, label.trim(), address.trim());
    onChanged();
  });
  const remove = useAction(async () => {
    await removeSavedPlace(api, place.id);
    onChanged();
  });

  const changed = label.trim() !== place.label || address.trim() !== place.address;
  const valid = label.trim().length > 0 && address.trim().length > 0;
  const busy = save.pending || remove.pending;

  return (
    <div className="saved-card">
      <div className="saved-head">
        <button type="button" className="saved-hit" onClick={onToggle} aria-expanded={open}>
          <span className="grow">
            <span className="saved-name">{place.label}</span>
            <span className="body ellipsis saved-address">{place.address}</span>
          </span>
          {/* Shut, the card said nothing about opening. The pencil «Профиль» puts
              beside the name says it here too, and makes way for the bin. */}
          {!open && <Icon name="edit_outlined" size={16} style={{ color: 'var(--muted)' }} />}
        </button>
        {open && (
          <IconButton
            icon="delete_outline"
            size={18}
            tone="stop"
            label="Удалить адрес"
            disabled={busy}
            style={{ marginRight: 14 }}
            onClick={() => void remove.run()}
          />
        )}
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ height: 1, background: 'var(--wash)', marginBottom: 12 }} />
          <input
            className="sfield"
            value={label}
            placeholder="Название"
            onChange={(event) => setLabel(event.target.value)}
          />
          <div style={{ marginTop: 8 }}>
            <AddressField value={address} onChange={setAddress} placeholder="Адрес" />
          </div>
          <ErrorNote error={save.error ?? remove.error} />
          <div style={{ marginTop: 10 }}>
            <Button
              size="field"
              busy={save.pending}
              disabled={!valid || !changed}
              onClick={() => void save.run()}
            >
              Сохранить
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
